import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import ModalCloseButton from './ModalCloseButton';

interface ImageAttributionModalProps {
  isOpen: boolean;
  onClose: () => void;
  imageUrl: string;
  itemName: string;
  token?: string;
  photographerName?: string;
  photographerUsername?: string;
  unsplashUrl?: string;
  onChangeImage?: () => void;
  isGuestToken?: boolean;
}

const ImageAttributionModal: React.FC<ImageAttributionModalProps> = ({
  isOpen,
  onClose,
  imageUrl,
  itemName,
  token,
  photographerName,
  photographerUsername,
  unsplashUrl,
  onChangeImage,
  isGuestToken
}) => {
  const [imageLoading, setImageLoading] = useState(true);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);

  // Progressive image loading: Load thumbnail first, then full-size
  useEffect(() => {
    if (!isOpen || !imageUrl) {
      setBlobUrl(null);
      setThumbnailUrl(null);
      return;
    }

    setImageLoading(true);
    setBlobUrl(null);
    setThumbnailUrl(null);

    // If it's an external URL (Unsplash), use directly
    if (imageUrl.startsWith('http') && !imageUrl.includes('/api/')) {
      setBlobUrl(imageUrl);
      setImageLoading(false);
      return;
    }

    // For API endpoints, fetch with progressive loading (thumbnail first, then full-size)
    const fetchImages = async () => {
      try {
        const headers: HeadersInit = {};
        if (token && !isGuestToken) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        // Load thumbnail first for instant display
        const thumbUrl = imageUrl.includes('?') ? `${imageUrl}&thumb=true` : `${imageUrl}?thumb=true`;
        const thumbResponse = await fetch(thumbUrl, { headers });
        
        if (thumbResponse.ok) {
          const thumbBlob = await thumbResponse.blob();
          const thumbBlobUrl = URL.createObjectURL(thumbBlob);
          setThumbnailUrl(thumbBlobUrl);
          setImageLoading(false);
        }

        // Then load full-size in background
        const response = await fetch(imageUrl, { headers });
        if (!response.ok) {
          console.error('Failed to fetch full-size image:', response.status);
          return;
        }

        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        setBlobUrl(url);
      } catch (error) {
        console.error('Error fetching image:', error);
        setImageLoading(false);
      }
    };

    fetchImages();

    // Cleanup blob URLs on unmount
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
      if (thumbnailUrl) {
        URL.revokeObjectURL(thumbnailUrl);
      }
    };
  }, [isOpen, imageUrl, token, isGuestToken]);

  if (!isOpen) return null;

  const hasAttribution = photographerName && photographerUsername;
  const photographerUrl = photographerUsername 
    ? `https://unsplash.com/@${photographerUsername}?utm_source=tidy&utm_medium=referral`
    : null;
  const unsplashHomeUrl = 'https://unsplash.com?utm_source=tidy&utm_medium=referral';
  const photoUrl = unsplashUrl ? `${unsplashUrl}?utm_source=tidy&utm_medium=referral` : null;

  return createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100000,
        padding: '0',
      }}
      onClick={onClose}
    >
      {/* Modal container - sized to image */}
      <div
        style={{
          position: 'relative',
          width: '100%',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          background: 'transparent',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button - hidden during loading to avoid flash */}
        {!imageLoading && (
          <ModalCloseButton
            onClick={onClose}
            style={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              width: '36px',
              height: '36px',
              background: 'rgba(0, 0, 0, 0.4)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              color: '#ffffff',
              zIndex: 10001,
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
            }}
          />
        )}

        {/* Image - show full-size if available, otherwise thumbnail */}
        {(blobUrl || thumbnailUrl) && (
          <img
            src={(blobUrl || thumbnailUrl) as string}
            alt={itemName}
            onLoad={() => setImageLoading(false)}
            onError={() => setImageLoading(false)}
            style={{
              width: '100%',
              height: 'auto',
              display: imageLoading ? 'none' : 'block',
              borderRadius: '0',
            }}
          />
        )}
        {imageLoading && (
          <div style={{ color: '#ffffff', fontSize: '16px', padding: '40px' }}>Loading image...</div>
        )}

        {/* Bottom bar - always shown */}
        {!imageLoading && (
          <div
            style={{
              background: 'rgba(0, 0, 0, 0.8)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              padding: '16px 20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '16px',
              borderTop: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '0',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Attribution text or item name */}
            <div
              style={{
                flex: 1,
                fontSize: '13px',
                color: 'rgba(255, 255, 255, 0.8)',
                lineHeight: '1.5',
              }}
            >
              {hasAttribution ? (
                <>
                  Photo by{' '}
                  {photographerUrl ? (
                    <a
                      href={photographerUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: '#ffffff',
                        fontWeight: 500,
                        textDecoration: 'underline',
                      }}
                    >
                      {photographerName}
                    </a>
                  ) : (
                    <span style={{ color: '#ffffff', fontWeight: 500 }}>
                      {photographerName}
                    </span>
                  )}
                  {' '}on{' '}
                  <a
                    href={unsplashHomeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: '#ffffff',
                      fontWeight: 500,
                      textDecoration: 'underline',
                    }}
                  >
                    Unsplash
                  </a>
                </>
              ) : (
                <span style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '14px' }}>
                  {itemName}
                </span>
              )}
            </div>

            {/* Right side buttons */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {/* View on Unsplash button - only show for non-guest users */}
              {photoUrl && !isGuestToken && (
                <a
                  href={photoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    padding: '6px 12px',
                    background: 'rgba(255, 255, 255, 0.15)',
                    color: '#ffffff',
                    borderRadius: '6px',
                    textDecoration: 'none',
                    fontSize: '12px',
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                    transition: 'background 0.2s',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.25)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                  }}
                >
                  View on Unsplash
                </a>
              )}
              
              {/* Change image icon button */}
              {onChangeImage && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onChangeImage();
                  }}
                  style={{
                    width: '32px',
                    height: '32px',
                    padding: 0,
                    background: 'transparent',
                    border: 'none',
                    color: '#ffffff',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '20px',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                  title="Change image"
                >
                  ✏️  
                </button>
              )}
            </div>
        </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default ImageAttributionModal;
